/**
 * audiences.ts (MKT-REDESIGN-01 M2, 2026-09-03) — saved audiences built from
 * Pegasus FACTS (pegasus_customer_fact: plan status & expiry, last service,
 * equipment & age, likely refrigerant, geography, value, lead source, consent),
 * resolved by the database (pegasus_audience_members / _count) so the same
 * spec gives the same list on Marketing, in a campaign, and at send time.
 * Dynamic = re-evaluated each time it's used; static = the custnos frozen
 * when it was saved.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CampaignRecipient } from "./campaigns";

export type EquipmentKind = "furnace" | "ac" | "heat-pump" | "boiler" | "mini-split" | "water-heater";
export type Refrigerant = "R-22" | "R-410A" | "R-454B";

export interface AudienceSpec {
  active_only?: boolean;
  plan?: "any" | "member" | "lapsed" | "none";
  plan_expires_within_days?: number | null;
  plan_expired_within_days?: number | null;
  last_service_over_days?: number | null;
  include_never_served?: boolean;
  last_service_within_days?: number | null;
  equipment?: EquipmentKind[];
  ac_installed_before?: number | null;
  furnace_installed_before?: number | null;
  system_older_than_years?: number | null;
  refrigerant?: Refrigerant | null;
  city?: string[];
  zip?: string[];
  zone?: string[];
  has_email?: boolean;
  has_mobile?: boolean;
  ltv_min_cents?: number | null;
  ltv_max_cents?: number | null;
  lead_source?: string[];
  exclude_balance?: boolean;
  exclude_custnos?: string[];
  /** Static audiences: the frozen list. */
  custnos?: string[];
}

export interface SavedAudience {
  id: string;
  name: string;
  description: string | null;
  spec: AudienceSpec;
  mode: "dynamic" | "static";
  static_custnos: string[] | null;
  last_count: number | null;
  last_sendable: number | null;
  last_counted_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface AudienceCount {
  matched: number;
  sendable: number;
  no_endpoint: number;
  unsubscribed: number;
  /** M6, texts only: has a cell but no marketing-text opt-in on record. */
  not_opted_in?: number;
  sample: string[];
  facts_as_of: string | null;
}

export interface AudienceMember {
  custno: string; name: string | null; email: string | null; mobile: string | null; city: string | null; zip: string | null;
  plan_status: string; sendable: boolean; exclude_reason: string | null;
}

export const AUDIENCES_KEY = ["audiences"] as const;

export const EQUIPMENT_LABEL: Record<EquipmentKind, string> = {
  furnace: "Furnace", ac: "A/C", "heat-pump": "Heat pump", boiler: "Boiler", "mini-split": "Mini-split", "water-heater": "Water heater",
};

/** Starter audiences — the ones every HVAC shop runs. Save one to make it yours. */
export const STARTER_AUDIENCES: { key: string; name: string; description: string; spec: AudienceSpec }[] = [
  { key: "all", name: "Everyone with an email", description: "Every active customer we can email.", spec: { has_email: true } },
  { key: "members", name: "Plan members", description: "Customers with an active maintenance plan.", spec: { plan: "member" } },
  { key: "expiring", name: "Plans expiring in 60 days", description: "Members whose plan ends within two months — renew before it lapses.", spec: { plan_expires_within_days: 60 } },
  { key: "lapsed", name: "Lapsed in the last 6 months", description: "Plans that ended within six months — the easiest win-back.", spec: { plan_expired_within_days: 180 } },
  { key: "winback", name: "No visit in 18 months", description: "Customers we've served before but not lately.", spec: { last_service_over_days: 540 } },
  { key: "r410", name: "R-410A owners, A/C older than 10 years", description: "The R-454B story — cooling systems from before 2016 still on R-410A.", spec: { refrigerant: "R-410A", ac_installed_before: 2016 } },
  { key: "old-furnace", name: "Furnace 15+ years old", description: "Heating systems installed before 2011.", spec: { equipment: ["furnace"], furnace_installed_before: 2011 } },
  { key: "r22", name: "R-22 cooling systems", description: "A/C installed before 2010 — refrigerant no longer made.", spec: { refrigerant: "R-22" } },
];

/** Plain words for a spec — the audience's own description and the campaign's audience label. */
export function describeSpec(spec: AudienceSpec): string {
  const bits: string[] = [];
  if (spec.custnos?.length) bits.push(`${spec.custnos.length.toLocaleString()} specific customers`);
  if (spec.plan && spec.plan !== "any") bits.push(spec.plan === "member" ? "plan members" : spec.plan === "lapsed" ? "lapsed members" : "no plan");
  if (spec.plan_expires_within_days) bits.push(`plan expires within ${spec.plan_expires_within_days} days`);
  if (spec.plan_expired_within_days) bits.push(`plan ended within ${spec.plan_expired_within_days} days`);
  if (spec.last_service_over_days) bits.push(`no visit in ${months(spec.last_service_over_days)}${spec.include_never_served ? " (or never)" : ""}`);
  if (spec.last_service_within_days) bits.push(`visited in the last ${months(spec.last_service_within_days)}`);
  if (spec.equipment?.length) bits.push(`has ${spec.equipment.map((e) => EQUIPMENT_LABEL[e]).join(" or ")}`);
  if (spec.ac_installed_before) bits.push(`A/C installed before ${spec.ac_installed_before}`);
  if (spec.furnace_installed_before) bits.push(`furnace installed before ${spec.furnace_installed_before}`);
  if (spec.system_older_than_years) bits.push(`a system ${spec.system_older_than_years}+ years old`);
  if (spec.refrigerant) bits.push(`likely ${spec.refrigerant}`);
  if (spec.city?.length) bits.push(spec.city.join(", "));
  if (spec.zip?.length) bits.push(`ZIP ${spec.zip.join(", ")}`);
  if (spec.zone?.length) bits.push(`zone ${spec.zone.join(", ")}`);
  if (spec.has_email) bits.push("has email");
  if (spec.has_mobile) bits.push("has a cell");
  if (spec.ltv_min_cents) bits.push(`lifetime $${Math.round(spec.ltv_min_cents / 100).toLocaleString()}+`);
  if (spec.ltv_max_cents) bits.push(`lifetime under $${Math.round(spec.ltv_max_cents / 100).toLocaleString()}`);
  if (spec.lead_source?.length) bits.push(`from ${spec.lead_source.join(", ")}`);
  if (spec.exclude_balance) bits.push("no open balance");
  if (spec.active_only === false) bits.push("including inactive");
  return bits.length ? bits.join(" · ") : "All active customers";
}
function months(days: number): string {
  const m = Math.round(days / 30);
  return m >= 12 && m % 12 === 0 ? `${m / 12} year${m === 12 ? "" : "s"}` : `${m} months`;
}

/** Drop empty keys so specs compare + store cleanly. */
export function cleanSpec(spec: AudienceSpec): AudienceSpec {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(spec)) {
    if (v == null || v === "" || v === "any" || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (k === "active_only" && v === true) continue;
    out[k] = v;
  }
  return out as AudienceSpec;
}

export function useAudiences() {
  return useQuery({
    queryKey: AUDIENCES_KEY,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<SavedAudience[]> => {
      const { data, error } = await supabase.from("pegasus_audience").select("*").is("archived_at", null).order("name");
      if (error) throw error;
      return (data ?? []) as SavedAudience[];
    },
  });
}

export function useAudienceCount(spec: AudienceSpec | null, channel: "email" | "sms" = "email") {
  const key = JSON.stringify(spec ?? {});
  return useQuery({
    queryKey: ["audience_count", key, channel],
    enabled: spec != null,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<AudienceCount> => {
      const { data, error } = await supabase.rpc("pegasus_audience_count", { p_spec: spec ?? {}, p_channel: channel });
      if (error) throw error;
      return data as AudienceCount;
    },
  });
}

/** The full list for a spec — the campaign recipients (sendable only) plus the exclusions. */
export async function resolveAudience(spec: AudienceSpec, channel: "email" | "sms" = "email"): Promise<{ recipients: CampaignRecipient[]; members: AudienceMember[] }> {
  const members: AudienceMember[] = [];
  const page = 1000;
  for (let from = 0; ; from += page) {
    const { data, error } = await supabase.rpc("pegasus_audience_members", { p_spec: spec, p_channel: channel }).range(from, from + page - 1);
    if (error) throw error;
    const rows = (data ?? []) as AudienceMember[];
    members.push(...rows);
    if (rows.length < page || members.length >= 20000) break;
  }
  const recipients: CampaignRecipient[] = members
    .filter((m) => m.sendable)
    .map((m) => ({ custno: m.custno, to: channel === "sms" ? (m.mobile ?? "") : (m.email ?? ""), name: m.name }))
    .filter((r) => r.to);
  return { recipients, members };
}

export function useSaveAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; name: string; description?: string | null; spec: AudienceSpec; mode: "dynamic" | "static"; count?: AudienceCount | null }): Promise<SavedAudience> => {
      const { data: u } = await supabase.auth.getUser();
      let staticCustnos: string[] | null = null;
      if (v.mode === "static") {
        const { members } = await resolveAudience(v.spec);
        staticCustnos = members.map((m) => m.custno);
      }
      const row = {
        name: v.name.trim(), description: v.description?.trim() || describeSpec(v.spec), spec: cleanSpec(v.spec), mode: v.mode,
        static_custnos: staticCustnos,
        last_count: v.count?.matched ?? null, last_sendable: v.count?.sendable ?? null, last_counted_at: v.count ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };
      if (v.id) {
        const { data, error } = await supabase.from("pegasus_audience").update(row).eq("id", v.id).select("*").single();
        if (error) throw error;
        return data as SavedAudience;
      }
      const { data, error } = await supabase.from("pegasus_audience").insert({ ...row, created_by: u.user?.email ?? null }).select("*").single();
      if (error) throw error;
      return data as SavedAudience;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AUDIENCES_KEY }),
  });
}

export function useArchiveAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_audience").update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AUDIENCES_KEY }),
  });
}

/** The spec a saved audience resolves with: dynamic = its filters today; static = the frozen list. */
export function specFor(a: SavedAudience): AudienceSpec {
  return a.mode === "static" && a.static_custnos?.length ? { custnos: a.static_custnos, active_only: false } : a.spec;
}

export function useRefreshFacts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ rows: number; seconds: number; at: string }> => {
      const { data, error } = await supabase.rpc("pegasus_customer_fact_refresh_now");
      if (error) throw error;
      return data as { rows: number; seconds: number; at: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["audience_count"] }),
  });
}
